import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, MessageSquare, Printer } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useBubbly } from '@/components/bubbly/BubblyContext';
import { useKbArticle } from '@/hooks/useKbArticles';
import { cn } from '@/lib/utils';

/**
 * /resources/:slug — renders one knowledge-base guide as a readable article.
 * Same markdown Mr. Bubbles answers from; managed on /admin/knowledge.
 */

export default function ResourceArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useKbArticle(slug);
  const { openBubbly } = useBubbly();

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2" data-app-chrome>
        <Link
          to="/resources"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brix-muted hover:text-brix-ink"
        >
          <ArrowLeft size={15} /> All resources
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border bg-brix-surface px-3 py-1.5 text-xs font-semibold text-brix-ink hover:border-brix-primary/40"
          >
            <Printer size={13} /> Print
          </button>
          <button
            type="button"
            onClick={() => openBubbly({ expanded: true })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brix-primary px-3 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95"
          >
            <MessageSquare size={13} /> Ask Mr. Bubbles
          </button>
        </div>
      </div>

      {isLoading && (
        <p className="py-16 text-center text-sm text-brix-muted">
          <Loader2 size={15} className="mr-1 inline animate-spin" /> Loading…
        </p>
      )}
      {error && (
        <div className="rounded-xl border border-brix-border bg-brix-surface p-6 text-center">
          <p className="text-sm font-semibold text-brix-ink">This guide isn't available.</p>
          <p className="mt-1 text-xs text-brix-muted">
            It may have been renamed —{' '}
            <Link to="/resources" className="font-semibold text-brix-primary hover:underline">
              browse the library
            </Link>{' '}
            or ask Mr. Bubbles.
          </p>
        </div>
      )}

      {article && (
        <article className="rounded-2xl border border-brix-border bg-brix-surface p-6 sm:p-8">
          <div
            className={cn(
              // Tailwind has no typography plugin here — style markdown manually.
              'text-[15px] leading-relaxed text-brix-ink',
              '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:text-brix-ink',
              '[&_h2]:mt-7 [&_h2]:border-b [&_h2]:border-brix-border [&_h2]:pb-1.5 [&_h2]:text-lg [&_h2]:font-bold',
              '[&_h3]:mt-5 [&_h3]:text-base [&_h3]:font-bold',
              '[&_p]:mt-3',
              '[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-6',
              '[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-6',
              '[&_li>p]:mt-0',
              '[&_strong]:font-semibold',
              '[&_a]:font-semibold [&_a]:text-brix-primary [&_a]:underline [&_a]:underline-offset-2',
              '[&_blockquote]:mt-3 [&_blockquote]:rounded-r-lg [&_blockquote]:border-l-4 [&_blockquote]:border-brix-primary/50 [&_blockquote]:bg-brix-primary-tint/30 [&_blockquote]:px-4 [&_blockquote]:py-2.5 [&_blockquote]:text-[14px]',
              '[&_code]:rounded [&_code]:bg-brix-surface-alt [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[13px]',
              '[&_table]:mt-3 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-brix-border [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_td]:border [&_td]:border-brix-border [&_td]:px-2 [&_td]:py-1.5 [&_td]:text-sm',
              '[&_hr]:my-6 [&_hr]:border-brix-border',
            )}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => {
                  const url = href ?? '#';
                  const external = /^https?:\/\//i.test(url);
                  return external ? (
                    <a href={url} target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  ) : (
                    <a href={url}>{children}</a>
                  );
                },
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>
          {article.updated_at && (
            <p className="mt-8 border-t border-brix-border pt-3 text-[11px] text-brix-muted" data-app-chrome>
              Last updated {new Date(article.updated_at).toLocaleDateString()} · Questions? Ask Mr.
              Bubbles or file a service request from the Service page.
            </p>
          )}
        </article>
      )}
    </div>
  );
}
